import React from 'react';
import classNames from 'classnames';
import { LinkedLogo } from '../../../../components';
import { LogoComponent } from '../../../../components/Logo/Logo';
import { useConfiguration } from '../../../../context/configurationContext';
// Footer logo is provided by Sharetribe hosted branding, not a local asset.

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';


// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentColumns, gridCss: css.gridCol1 },
  { contentCss: css.contentColumns, gridCss: css.gridCol2 },
  { contentCss: css.contentColumns, gridCss: css.gridCol3 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  const config = useConfiguration();
  const marketplaceName = config?.marketplaceName;
  const logoSettings = config?.branding?.logoSettings;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';


  // Limit the grid layout to a maximum of two columns to simplify the
  // structure and keep the layout consistent regardless of the configured
  // column count in the marketplace editor.
  const columnCount = Math.min(Math.max(numberOfColumns, 1), 2);

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={classNames(css.root, className)}
      rootClassName={rootClassName || css.sectionContainer}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={classNames(css.content, getContentCss(columnCount))}>
          <div className={css.brandSection}>
            <div className={css.brandLogo}>
              <LinkedLogo
                rootClassName={css.logoLink}
                logoClassName={css.logoWrapper}
                logoImageClassName={css.logoImage}
                linkToExternalSite={linkLogoToExternalSite}
                layout={logoLayout}
                alt={marketplaceName}
                renderLogo={({ layout, className, logoImageClassName, alt }) => (
                  <LogoComponent
                    layout={layout}
                    className={className}
                    logoImageClassName={logoImageClassName}
                    logoImageDesktop={null}
                    logoImageMobile={null}
                    logoSettings={logoSettings}
                    marketplaceName={alt || marketplaceName}
                  />
                )}
              />
            </div>
            
            {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null}
            <Field data={copyright} className={css.copyright} />
          </div>
          <div className={classNames(css.grid, getGridCss(columnCount))}>
            <BlockBuilder blocks={blocks} sectionId={sectionId} options={options} />
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
